/**
 * Portal del rol "user" — usuario logueado SIN rol admin (ni super).
 *
 * Acá caen los no-admin: `ProtectedAdminRoute` redirige a `USER_PORTAL_PATH` en vez
 * de rebotarlos a la home pública. Muestra estado "pendiente de asignación de rol"
 * + un portal básico (identidad, contacto, cerrar sesión). El super admin asigna el
 * rol en Clerk; recién ahí el usuario entra al panel.
 */
import { RedirectToSignIn, SignOutButton, useAuth, useUser } from "@clerk/clerk-react";
import { Navigate } from "react-router-dom";
import { Clock, LogOut, ShieldQuestion } from "lucide-react";
import { PortalCard, PortalEyebrow, PortalShell } from "@/components/portal/PortalShell";
import { FullBleedDarkLoading } from "@/components/admin/AdminStates";
import { useIsAdmin } from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";

/** Fuente única del path del portal (también usado en ProtectedAdminRoute + App). */
export const USER_PORTAL_PATH = "/portal";

export default function UserPortal() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const isAdmin = useIsAdmin();

  if (!isLoaded) {
    return <FullBleedDarkLoading label="Cargando portal…" />;
  }
  if (!isSignedIn) return <RedirectToSignIn />;
  if (isAdmin) return <Navigate to="/admin" replace />;

  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const name = user?.firstName?.trim() || "";

  return (
    <PortalShell>
      <PortalCard className="w-full max-w-md space-y-5 p-5 sm:p-6">
        <PortalEyebrow>{BRAND_PERSON_NAME || "Portal"}</PortalEyebrow>

        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-inset ring-amber-500/25">
            <Clock className="h-5 w-5 text-amber-400" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold tracking-tight text-white/95">
              Acceso pendiente
            </h1>
            <p className="mt-1 text-[13px] leading-relaxed text-white/55">
              {name ? (
                <>
                  Hola <span className="text-white/80">{name}</span>.{" "}
                </>
              ) : null}
              Tu cuenta está creada pero todavía no tiene un rol asignado. Un
              administrador debe habilitarte para que puedas entrar al panel.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 text-[12px] text-white/55">
          <div className="flex flex-wrap items-center gap-2 text-white/70">
            <ShieldQuestion className="h-4 w-4 shrink-0" aria-hidden />
            <span>Estado:</span>
            <span className="font-semibold text-amber-300">
              Pendiente de asignación
            </span>
          </div>
          {email ? (
            <div className="mt-1.5 truncate text-white/40">Sesión: {email}</div>
          ) : null}
        </div>

        <p className="text-[12px] leading-relaxed text-white/45">
          ¿Creés que es un error? Escribí a{" "}
          <a
            className="portal-press text-white/70 underline underline-offset-2"
            href={`mailto:${BRAND_SUPPORT_EMAIL}`}
          >
            {BRAND_SUPPORT_EMAIL}
          </a>
          .
        </p>

        <div className="flex flex-col-reverse gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <a
            href="/"
            className="portal-press inline-flex min-h-10 items-center justify-center text-[12px] text-white/50 hover:text-white/80 sm:justify-start"
          >
            ← Volver al sitio
          </a>
          <SignOutButton>
            <button
              type="button"
              className="portal-press inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-white/15 px-4 py-2 text-[12px] font-medium text-white/70 hover:bg-white/5 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden /> Cerrar sesión
            </button>
          </SignOutButton>
        </div>
      </PortalCard>
    </PortalShell>
  );
}
